import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const PRODUCTS_PER_PAGE_OPTIONS = [4, 8, 12, 20];

// Card size configurations
const CARD_SIZE_CONFIG = {
  small: {
    gridCols: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6',
    imageHeight: 'h-28',
    titleSize: 'text-sm',
    priceSize: 'text-base',
  },
  medium: {
    gridCols: 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
    imageHeight: 'h-36',
    titleSize: 'text-base',
    priceSize: 'text-lg',
  },
  large: {
    gridCols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3',
    imageHeight: 'h-48',
    titleSize: 'text-lg',
    priceSize: 'text-xl',
  },
};

const ProductGrid = ({ 
  products, 
  onAddToCart, 
  favorites, 
  onToggleFavorite, 
  onViewDetails,
  productsPerPage = 4,
  onProductsPerPageChange,
  cardSize = 'medium'
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const sizeConfig = CARD_SIZE_CONFIG[cardSize] || CARD_SIZE_CONFIG.medium;
  
  const totalPages = Math.ceil(products.length / productsPerPage);
  
  // Ensure current page is valid when total pages changes
  const validCurrentPage = Math.min(currentPage, Math.max(1, totalPages));
  if (validCurrentPage !== currentPage && totalPages > 0) {
    setCurrentPage(validCurrentPage);
  }
  
  const startIndex = (validCurrentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    // Scroll to top of product grid
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  // Generate page numbers to display (max 5 pages shown)
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Add ellipsis if needed
      if (start > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  if (products.length === 0) {
    return (
      <div className="mx-4 md:mx-8 lg:mx-16 mt-6">
        <div className="text-center py-16 bg-white rounded-2xl border border-[#0a1628]/20">
          <p className="text-[#0a1628]/80 text-lg font-medium">No products found matching your criteria.</p>
          <p className="text-[#0a1628]/50 text-sm mt-2">Try adjusting your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 md:mx-8 lg:mx-16 mt-6 mb-12">
      {/* Product Grid - Dynamic columns based on card size */}
      <div className={`grid ${sizeConfig.gridCols} gap-4 sm:gap-6`}>
        {currentProducts.map((product, index) => (
          <div 
            key={product.id} 
            className="product-card"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <ProductCard
              product={product}
              onAddToCart={onAddToCart}
              onToggleFavorite={onToggleFavorite}
              isFavorite={favorites.includes(product.id)}
              onViewDetails={onViewDetails}
              cardSize={cardSize}
            />
          </div>
        ))}
      </div>

      {/* Pagination - Left aligned */}
      {(totalPages > 1 || products.length > 4) && (
        <div className="flex flex-col items-start gap-4 mt-8">
          {/* Products Per Page Selector + Page Info */}
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-6">
            {/* Products Per Page Selector */}
            {onProductsPerPageChange && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#0a1628]/60">Show:</span>
                <Select
                  value={productsPerPage.toString()}
                  onValueChange={(value) => {
                    setCurrentPage(1);  // Reset to first page
                    onProductsPerPageChange(parseInt(value));
                  }}
                >
                  <SelectTrigger className="w-[80px] h-8 border-[#0a1628]/20 text-[#0a1628] bg-white" data-testid="products-per-page-selector">
                    <SelectValue placeholder="4" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTS_PER_PAGE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option.toString()}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-[#0a1628]/60">per page</span>
              </div>
            )}
            
            {/* Page Info */}
            <div className="text-sm text-[#0a1628]/60">
              Showing {startIndex + 1}-{Math.min(endIndex, products.length)} of {products.length} products
            </div>
          </div>
          
          {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-[#0a1628]/20 text-[#0a1628] hover:bg-[#0a1628]/10 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-[#0a1628]/50">...</span>
                ) : (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(page)}
                    className={currentPage === page 
                      ? "bg-[#0a1628] text-white hover:bg-[#0a1628]/90 min-w-[36px]" 
                      : "border-[#0a1628]/20 text-[#0a1628] hover:bg-[#0a1628]/10 min-w-[36px]"
                    }
                  >
                    {page}
                  </Button>
                )
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="border-[#0a1628]/20 text-[#0a1628] hover:bg-[#0a1628]/10 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
